import React, { useState, useMemo } from 'react';
import { useStore } from '../store/StoreContext';
import { Input } from '../components/UI/Input';
import { Button } from '../components/UI/Button';
import { Modal } from '../components/UI/Modal';
import { ImageUpload } from '../components/UI/ImageUpload';
import { generateId } from '../utils/helpers';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export const BusinessUnits = () => {
  const { state, addEntity, updateEntity, deleteEntity } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [editingBu, setEditingBu] = useState(null);
  const [activeBuForArchive, setActiveBuForArchive] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    logoUrl: ''
  });

  const bus = Object.values(state.businessUnits || {});
  
  const filteredBus = useMemo(() => {
    return bus.filter(bu => 
      bu.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bu.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [bus, searchQuery]);

  const openModal = (bu = null) => {
    if (bu) {
      setEditingBu(bu);
      setFormData(bu);
    } else {
      setEditingBu(null);
      setFormData({ name: '', description: '', website: '', logoUrl: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingBu) {
      await updateEntity('businessUnits', { ...formData, id: editingBu.id });
    } else {
      await addEntity('businessUnits', { ...formData, id: generateId('bu') });
    }
    setIsModalOpen(false);
  };

  const archivedProjects = activeBuForArchive 
    ? Object.values(state.projects || {}).filter(p => p.businessUnitId === activeBuForArchive.id && p.status === 'archived')
    : [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900 custom-scrollbar">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Business Units</h1>
          <p className="text-sm text-gray-400 font-medium">Manage organizational departments and their digital presence.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <SafeIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Search units..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-base-800 border border-base-700 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-primary transition-all"
            />
          </div>
          <Button onClick={() => openModal()} className="shadow-xl">
            <SafeIcon name="Plus" className="mr-2" /> Add Unit
          </Button>
        </div>
      </div>

      {filteredBus.length === 0 ? (
        <div className="h-[400px] flex flex-col items-center justify-center text-gray-600 bg-base-800/20 rounded-3xl border border-dashed border-base-700">
          <SafeIcon name="Briefcase" className="w-16 h-16 mb-4 opacity-10" />
          <p className="text-lg font-medium">No business units found</p>
          {searchQuery && <Button variant="ghost" onClick={() => setSearchQuery('')} className="mt-2">Clear Search</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredBus.map(bu => {
              const buProjects = Object.values(state.projects || {}).filter(p => p.businessUnitId === bu.id && p.status !== 'archived');
              const buArchivedCount = Object.values(state.projects || {}).filter(p => p.businessUnitId === bu.id && p.status === 'archived').length;
              
              return (
                <motion.div 
                  layout
                  key={bu.id} 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-base-800 rounded-2xl border border-base-700 p-6 flex flex-col hover:border-primary/50 transition-all group shadow-lg hover:shadow-primary/5"
                >
                  <div className="flex justify-between items-start mb-4">
                    <a 
                      href={bu.website || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={`w-14 h-14 rounded-2xl bg-base-900 border border-base-700 flex items-center justify-center overflow-hidden transition-transform hover:scale-110 shadow-inner ${!bu.website && 'pointer-events-none'}`}
                    >
                      {bu.logoUrl ? (
                        <img src={bu.logoUrl} alt={bu.name} className="w-full h-full object-contain p-2" />
                      ) : (
                        <span className="text-primary font-black text-2xl">{bu.name.charAt(0)}</span>
                      )}
                    </a>
                    <div className="flex gap-1">
                      <button onClick={() => openModal(bu)} className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-all">
                        <SafeIcon name="Edit2" className="w-4 h-4" />
                      </button>
                      <div className="bg-base-900 px-3 py-1 rounded-full text-[10px] font-mono text-gray-400 border border-base-700 flex items-center h-fit mt-1">
                        {buProjects.length} Active
                      </div>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold mb-1 text-white truncate">{bu.name}</h3>
                  <p className="text-sm text-gray-500 mb-6 line-clamp-2 min-h-[40px] leading-relaxed">{bu.description}</p>
                  
                  <div className="pt-4 border-t border-base-700 mt-auto flex flex-col gap-2">
                    <Button 
                      variant="ghost" 
                      className="w-full bg-base-900/50 border border-base-700 text-[10px] uppercase font-black tracking-widest py-3 hover:bg-base-700 transition-all"
                      onClick={() => { setActiveBuForArchive(bu); setIsArchiveModalOpen(true); }}
                    >
                      <SafeIcon name="Archive" className="w-3.5 h-3.5 mr-2" />
                      Archived Projects ({buArchivedCount})
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Existing Modals remain same, ensuring high-capacity data handling */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingBu ? "Update Unit" : "Register New Unit"}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <Input label="Unit Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. Creative Lab" />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-400">Description</label>
                <textarea 
                  className="bg-base-800 border border-base-700 rounded-xl px-3 py-3 text-sm text-white focus:outline-none focus:border-primary min-h-[120px] resize-none leading-relaxed" 
                  value={formData.description} 
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What is the focus of this unit?"
                />
              </div>
              <Input label="Official Website / Home URL" value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-6">
              <ImageUpload label="Identity Logo" currentUrl={formData.logoUrl} onUpload={(url) => setFormData({ ...formData, logoUrl: url })} />
              <div className="pt-4 border-t border-base-700/50">
                <Input label="OR Asset Link" value={formData.logoUrl} onChange={e => setFormData({ ...formData, logoUrl: e.target.value })} placeholder="https://..." />
                <p className="text-[10px] text-gray-500 mt-3 italic font-medium">External links will override uploaded files.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-6 border-t border-base-700">
            {editingBu && (
              <Button variant="danger" type="button" onClick={async () => { if (confirm('Delete unit? All associated data will be orphaned.')) { await deleteEntity('businessUnits', editingBu.id); setIsModalOpen(false); } }}>
                Delete Permanent
              </Button>
            )}
            <Button variant="ghost" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit" className="px-8">Save Unit</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isArchiveModalOpen} onClose={() => setIsArchiveModalOpen(false)} title={`Archives: ${activeBuForArchive?.name}`}>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          {archivedProjects.length === 0 ? (
            <div className="py-16 text-center text-gray-600">
              <SafeIcon name="Archive" className="w-12 h-12 mx-auto mb-4 opacity-10" />
              <p className="font-medium">The vault is empty.</p>
            </div>
          ) : (
            archivedProjects.map(p => (
              <div key={p.id} className="bg-base-900 p-4 rounded-2xl border border-base-700 flex justify-between items-center group hover:border-base-600 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-10 rounded-full shadow-lg" style={{ backgroundColor: p.color }}></div>
                  <div>
                    <div className="font-bold text-white mb-0.5">{p.name}</div>
                    <div className="text-[10px] text-gray-500 uppercase font-black tracking-tighter">Vault ID: {p.id.split('_')[1]}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => await updateEntity('projects', { ...p, status: 'active' })} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-black hover:bg-primary hover:text-white transition-all border border-primary/20 uppercase tracking-widest">
                    <SafeIcon name="RefreshCw" className="w-3.5 h-3.5" />
                    Restore
                  </button>
                  <button onClick={async () => { if (confirm('Purge this project permanently?')) await deleteEntity('projects', p.id); }} className="p-3 text-gray-600 hover:text-red-500 transition-colors">
                    <SafeIcon name="Trash2" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>
    </motion.div>
  );
};